import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  MdAttachMoney,
  MdChatBubble,
  MdCreditCard,
  MdEmail,
  MdFavorite,
  MdHome,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdList,
  MdLocalOffer,
  MdLocalShipping,
  MdLock,
  MdNotifications,
  MdPerson,
  MdReceipt,
  MdSettings,
  MdStore,
} from 'react-icons/md';
import { FaPaypal } from 'react-icons/fa';
import { useIntl } from '../../util/reactIntl';

import NamedLink from '../NamedLink/NamedLink';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';
import { getUsername } from '../../util/dataExtractors';
import { PAYPAL_ENABLED } from '../../util/paypalPhaseOut';
import {
  BUYING,
  MESSAGES_LINK,
  OVERVIEW_LINK,
  SELLING,
  SIDE_LINKS,
  isLinkCurrent,
} from './dashboardNav';
import { useOwnStorePaths } from './useDashboardSide';

import css from './DashboardSidebar.module.css';

const sidebarIcons = {
  account: MdSettings,
  card: MdCreditCard,
  contact: MdEmail,
  downArrow: MdKeyboardArrowDown,
  favorites: MdFavorite,
  home: MdHome,
  listings: MdList,
  messages: MdChatBubble,
  notifications: MdNotifications,
  offers: MdLocalOffer,
  orders: MdReceipt,
  password: MdLock,
  paypal: FaPaypal,
  payout: MdAttachMoney,
  payouts: MdCreditCard,
  profile: MdPerson,
  shipping: MdLocalShipping,
  shop: MdStore,
  upArrow: MdKeyboardArrowUp,
};

const SidebarIcon = ({ name }) => {
  const Icon = sidebarIcons[name];
  return Icon ? <Icon aria-hidden="true" focusable="false" /> : null;
};

// Settings: the account pages, plus profile and text notifications
const settingsLinks = [
  {
    key: 'profile',
    name: 'ProfileSettingsPage',
    labelId: 'DashboardSidebar.link.profileSettings',
    icon: 'profile',
  },
  {
    key: 'notifications',
    name: 'NotificationsPage',
    labelId: 'DashboardSidebar.link.notifications',
    icon: 'notifications',
  },
];

const accountLinks = [
  {
    key: 'contact',
    name: 'ContactDetailsPage',
    labelId: 'DashboardSidebar.account.contactDetails',
    icon: 'contact',
  },
  {
    key: 'shipping',
    name: 'ShippingDetailsPage',
    labelId: 'DashboardSidebar.account.shippingDetails',
    icon: 'shipping',
  },
  {
    key: 'password',
    name: 'PasswordChangePage',
    labelId: 'DashboardSidebar.account.password',
    icon: 'password',
  },
  {
    key: 'stripe',
    name: 'StripePayoutPage',
    labelId: 'DashboardSidebar.account.payoutDetails',
    icon: 'payout',
  },
  ...(PAYPAL_ENABLED
    ? [
        {
          key: 'paypal',
          name: 'PayPalMerchantPage',
          labelId: 'DashboardSidebar.account.paypalAccount',
          icon: 'paypal',
        },
      ]
    : []),
  {
    key: 'cards',
    name: 'PaymentMethodsPage',
    labelId: 'DashboardSidebar.account.paymentMethods',
    icon: 'card',
  },
];

const routeMatchByPathname = (pathname, name) => {
  if (!pathname) {
    return false;
  }

  if (name === 'LandingPage') {
    return pathname === '/';
  }

  if (name === 'DashboardPage') {
    return pathname === '/dashboard';
  }

  if (name === 'InboxPage') {
    return (
      pathname.startsWith('/inbox') || pathname.startsWith('/order') || pathname.startsWith('/sale')
    );
  }

  if (name === 'MessagesPage') {
    return pathname.startsWith('/messages');
  }

  if (name === 'OffersPage') {
    return pathname.startsWith('/offers');
  }

  if (name === 'StripePayoutPage') {
    return pathname.startsWith('/account/payments');
  }

  if (name === 'PayPalMerchantPage') {
    return pathname.startsWith('/account/paypal-merchant');
  }

  if (name === 'PaymentMethodsPage') {
    return pathname.startsWith('/account/payment-methods');
  }

  if (name === 'ContactDetailsPage') {
    return pathname.startsWith('/account/contact-details');
  }

  if (name === 'ShippingDetailsPage') {
    return pathname.startsWith('/account/shipping-details');
  }

  if (name === 'PasswordChangePage') {
    return pathname.startsWith('/account/change-password');
  }

  if (name === 'ManageListingsPage') {
    return pathname === '/listings';
  }

  if (name === 'NotificationsPage') {
    return pathname === '/notifications';
  }

  if (name === 'ProfileSettingsPage') {
    return pathname === '/profile-settings';
  }

  return false;
};

const DashboardSidebar = ({ onNavigate = null }) => {
  const location = useLocation();
  const history = useHistory();
  const intl = useIntl();
  const currentUser = useSelector(state => state.user.currentUser);
  const currentUserHasListings = useSelector(state => state.user.currentUserHasListings);
  const username = getUsername(currentUser);
  const userId = currentUser?.id?.uuid;
  const ownStorePaths = useOwnStorePaths();
  const pathname = location?.pathname || '';
  const isSettingsPath =
    pathname.startsWith('/account') ||
    pathname === '/profile-settings' ||
    pathname === '/notifications';
  const [isSettingsOpen, setIsSettingsOpen] = useState(isSettingsPath);

  useEffect(() => {
    if (isSettingsPath) {
      setIsSettingsOpen(true);
    }
  }, [isSettingsPath]);

  const triggerNavigate = () => {
    if (typeof onNavigate === 'function') {
      onNavigate();
    }
  };

  const redirectToOwnStore = () => {
    if (!userId && !username) {
      return;
    }

    const routeName = username ? 'StorePage' : 'ProfilePage';
    const params = username ? { username } : { id: userId };
    history.push(createResourceLocatorString(routeName, routeConfiguration(), params, {}));
    triggerNavigate();
  };

  const renderLink = link => {
    const isCurrent = isLinkCurrent(link, pathname, ownStorePaths);
    if (link.isOwnStore) {
      return (
        <button
          key={link.key}
          type="button"
          className={classNames(css.link, css.buttonLink, {
            [css.current]: isCurrent,
            [css.disabled]: !currentUserHasListings,
          })}
          onClick={redirectToOwnStore}
          disabled={!currentUserHasListings}
        >
          <SidebarIcon name={link.icon} />
          {intl.formatMessage({ id: link.labelId })}
        </button>
      );
    }
    return (
      <NamedLink
        key={link.key}
        className={classNames(css.link, { [css.current]: isCurrent })}
        name={link.name}
        params={link.params}
        onClick={triggerNavigate}
      >
        <SidebarIcon name={link.icon} />
        {intl.formatMessage({ id: link.labelId })}
      </NamedLink>
    );
  };

  return (
    <nav
      className={css.root}
      aria-label={intl.formatMessage({ id: 'DashboardSidebar.navigationAriaLabel' })}
    >
      {renderLink(OVERVIEW_LINK)}

      {[SELLING, BUYING].map(side => (
        <div key={side} className={css.group}>
          <span className={css.groupLabel}>
            {intl.formatMessage({ id: `DashboardNav.side.${side}` })}
          </span>
          {SIDE_LINKS[side].map(renderLink)}
        </div>
      ))}

      <div className={css.group}>{renderLink(MESSAGES_LINK)}</div>

      <button
        type="button"
        className={classNames(css.link, css.buttonLink, css.accountToggle, {
          [css.current]: isSettingsPath,
        })}
        onClick={() => setIsSettingsOpen(open => !open)}
        aria-expanded={isSettingsOpen}
      >
        <span className={css.accountLabel}>
          <SidebarIcon name="account" />
          {intl.formatMessage({ id: 'DashboardNav.settings' })}
        </span>
        <SidebarIcon name={isSettingsOpen ? 'upArrow' : 'downArrow'} />
      </button>

      {isSettingsOpen ? (
        <div className={css.accountChildren}>
          {[...settingsLinks, ...accountLinks].map(link => (
            <NamedLink
              key={link.key}
              className={classNames(css.link, css.accountChildLink, {
                [css.current]: routeMatchByPathname(pathname, link.name),
              })}
              name={link.name}
              onClick={triggerNavigate}
            >
              <SidebarIcon name={link.icon} />
              {intl.formatMessage({ id: link.labelId })}
            </NamedLink>
          ))}
        </div>
      ) : null}
    </nav>
  );
};

export default DashboardSidebar;
